import type { AnalogPricePoint, StockAnalogMatch } from '@/mocks/stockAnalogMock';

export type DailyOutlookDay = {
  sessionOffset: number;
  projectedDate: string;
  weekdayLabel: string;
  median: number;
  avg: number;
  positive: number;
  total: number;
  min: number;
  max: number;
  cumulativeMedian: number;
  call: string;
  bestAnalog: { date: string; value: number } | null;
  worstAnalog: { date: string; value: number } | null;
};

export type DailyOutlook = {
  days: DailyOutlookDay[];
  cumulativePath: { sessionOffset: number; projectedDate: string; cumulativeMedian: number }[];
};

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/** Skip weekends when walking forward from a reference session. */
export function addTradingDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T12:00:00`);
  const step = days >= 0 ? 1 : -1;
  let remaining = Math.abs(days);
  while (remaining > 0) {
    d.setDate(d.getDate() + step);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) remaining -= 1;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function weekdayLabel(isoDate: string): string {
  try {
    const [y, mo, d] = isoDate.split('-').map(Number);
    return new Date(y, mo - 1, d).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

function extractDailyReturns(path: AnalogPricePoint[]): Map<number, number> {
  const byT = new Map<number, number>();
  for (const pt of path) {
    if (typeof pt.t === 'number' && typeof pt.price === 'number') {
      byT.set(pt.t, pt.price);
    }
  }
  const out = new Map<number, number>();
  const maxT = Math.max(...byT.keys(), 0);
  for (let t = 1; t <= maxT; t += 1) {
    const prev = byT.get(t - 1);
    const cur = byT.get(t);
    if (prev != null && cur != null && prev > 0) {
      out.set(t, ((cur / prev - 1) * 100));
    }
  }
  return out;
}

function formatPct(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function buildDayCall(
  day: Omit<DailyOutlookDay, 'call'>,
  sessionOffset: number,
): string {
  const { median, positive, total, cumulativeMedian, weekdayLabel: wd } = day;
  const neg = total - positive;
  const pctPos = total > 0 ? positive / total : 0;

  const opener =
    sessionOffset === 1
      ? 'Next session'
      : sessionOffset === 2
        ? 'Day 2'
        : `Day ${sessionOffset}`;

  if (pctPos >= 0.7 && median >= 0.25) {
    return `${opener} (${wd}): expect a bounce — median ${formatPct(median)} (${positive}/${total} analogs higher). Running tally from close ~${formatPct(cumulativeMedian)}.`;
  }
  if (pctPos <= 0.35 && median <= -0.25) {
    return `${opener} (${wd}): expect more selling — median ${formatPct(median)} (${neg}/${total} analogs lower). Cumulative ~${formatPct(cumulativeMedian)} from close.`;
  }
  if (Math.abs(median) < 0.15) {
    return `${opener} (${wd}): likely flat/chop — median ${formatPct(median)} (${positive}-${neg} split). Cumulative ~${formatPct(cumulativeMedian)}.`;
  }
  if (median > 0) {
    return `${opener} (${wd}): lean slightly higher — median ${formatPct(median)} (${positive}/${total} up). Cumulative ~${formatPct(cumulativeMedian)}.`;
  }
  return `${opener} (${wd}): lean slightly lower — median ${formatPct(median)} (${neg}/${total} down). Cumulative ~${formatPct(cumulativeMedian)}.`;
}

/**
 * Aggregate session-by-session forward expectations from analog ^GSPC price paths.
 * Session +1 = first trading day after the reference close.
 */
export function buildDailyOutlook(
  matches: StockAnalogMatch[],
  referenceDate: string,
  horizonSessions = 7,
): DailyOutlook | null {
  if (!referenceDate || !matches.length) return null;

  const perAnalog: { date: string; daily: Map<number, number> }[] = [];
  for (const match of matches) {
    const path = match.pricePath ?? [];
    if (path.length < 2) continue;
    perAnalog.push({ date: match.date, daily: extractDailyReturns(path) });
  }
  if (!perAnalog.length) return null;

  const days: DailyOutlookDay[] = [];

  for (let offset = 1; offset <= horizonSessions; offset += 1) {
    const vals: { value: number; analogDate: string }[] = [];
    for (const row of perAnalog) {
      const v = row.daily.get(offset);
      if (typeof v === 'number' && !Number.isNaN(v)) {
        vals.push({ value: v, analogDate: row.date });
      }
    }
    if (!vals.length) break;

    const values = vals.map((v) => v.value);
    const med = median(values);
    const cumulativeMedian = median(
      perAnalog.map((row) => {
        let factor = 1;
        for (let t = 1; t <= offset; t += 1) {
          const v = row.daily.get(t);
          if (typeof v === 'number') factor *= 1 + v / 100;
        }
        return (factor - 1) * 100;
      }),
    );

    const sorted = [...vals].sort((a, b) => b.value - a.value);
    const projectedDate = addTradingDays(referenceDate, offset);

    const base: Omit<DailyOutlookDay, 'call'> = {
      sessionOffset: offset,
      projectedDate,
      weekdayLabel: weekdayLabel(projectedDate),
      median: med,
      avg: values.reduce((a, b) => a + b, 0) / values.length,
      positive: values.filter((v) => v > 0).length,
      total: values.length,
      min: Math.min(...values),
      max: Math.max(...values),
      cumulativeMedian,
      bestAnalog: sorted[0] ? { date: sorted[0].analogDate, value: sorted[0].value } : null,
      worstAnalog: sorted[sorted.length - 1]
        ? { date: sorted[sorted.length - 1].analogDate, value: sorted[sorted.length - 1].value }
        : null,
    };

    days.push({
      ...base,
      call: buildDayCall(base, offset),
    });
  }

  if (!days.length) return null;

  const cumulativePath = days.map((d) => ({
    sessionOffset: d.sessionOffset,
    projectedDate: d.projectedDate,
    cumulativeMedian: d.cumulativeMedian,
  }));

  return { days, cumulativePath };
}
